import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { CheckCircle2, ShieldCheck, LineChart, Building2 } from "lucide-react";
import heroImg from "@/assets/hero-professional.jpg";
import womanImg from "@/assets/professional-woman.jpg";
import limaImg from "@/assets/lima-city.jpg";
import FlagRibbon from "@/components/FlagRibbon";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export default function HomePage() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [form, setForm] = useState({ name: "", email: "" });
  const [errors, setErrors] = useState({});

  const onSubmit = (e) => {
    e.preventDefault();
    const errs = {};
    if (!form.name.trim()) errs.name = t("home.newsletter.required");
    if (!form.email.trim()) errs.email = t("home.newsletter.required");
    else if (!EMAIL_RE.test(form.email)) errs.email = t("home.newsletter.invalid_email");
    setErrors(errs);
    if (Object.keys(errs).length > 0) return;
    navigate("/thank_you");
  };

  const features = [
    {
      icon: LineChart,
      title: t("home.features.cards.one.title"),
      body: t("home.features.cards.one.body"),
    },
    {
      icon: ShieldCheck,
      title: t("home.features.cards.two.title"),
      body: t("home.features.cards.two.body"),
    },
    {
      icon: Building2,
      title: t("home.features.cards.three.title"),
      body: t("home.features.cards.three.body"),
    },
  ];

  return (
    <div data-testid="home-page">
      {/* Hero */}
      <section
        className="relative"
        style={{ backgroundColor: "#F3F4F6" }}
        data-testid="hero-section"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-24 grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <span
              className="inline-block text-xs font-semibold uppercase tracking-widest px-3 py-1 rounded-full"
              style={{ backgroundColor: "#0B1D3A", color: "#10B981" }}
            >
              {t("home.hero_eyebrow")}
            </span>
            <h1
              className="mt-5 text-4xl sm:text-5xl lg:text-6xl leading-[1.1] text-slate-900"
              style={{ fontFamily: "Merriweather, serif", fontWeight: 700 }}
              data-testid="hero-title"
            >
              {t("home.hero_title")}
            </h1>
            <p className="mt-6 text-lg text-slate-700 leading-relaxed max-w-xl">
              {t("home.hero_subtitle")}
            </p>
            <ul className="mt-8 space-y-3">
              {["one", "two", "three"].map((k) => (
                <li key={k} className="flex items-start gap-3">
                  <CheckCircle2
                    className="mt-1 h-5 w-5 flex-shrink-0"
                    style={{ color: "#10B981" }}
                  />
                  <span className="text-slate-800">
                    {t(`home.hero_bullets.${k}`)}
                  </span>
                </li>
              ))}
            </ul>
          </div>

          <div className="relative">
            <img
              src={heroImg}
              alt="Peruvian financial professional in Lima"
              className="w-full h-[420px] object-cover rounded-md border border-slate-200 shadow-lg"
              data-testid="hero-image"
            />
            <div
              className="absolute -bottom-6 -left-6 hidden lg:block h-24 w-24 rounded-md"
              style={{ backgroundColor: "#0B1D3A" }}
              aria-hidden="true"
            />
          </div>
        </div>
      </section>

      {/* Growth Section */}
      <section
        className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20"
        data-testid="growth-section"
      >
        <div className="max-w-3xl">
          <h2
            className="text-3xl sm:text-4xl text-slate-900"
            style={{ fontFamily: "Merriweather, serif", fontWeight: 700 }}
          >
            {t("home.growth_title")}
          </h2>
          <p className="mt-5 text-lg text-slate-700 leading-relaxed">
            {t("home.growth_body")}
          </p>
        </div>
      </section>

      <FlagRibbon />

      {/* Features */}
      <section
        style={{ backgroundColor: "#F3F4F6" }}
        className="py-20"
        data-testid="features-section"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <h2
              className="text-3xl sm:text-4xl text-slate-900"
              style={{ fontFamily: "Merriweather, serif", fontWeight: 700 }}
            >
              {t("home.features.title")}
            </h2>
            <p className="mt-4 text-slate-700">
              {t("home.features.subtitle")}
            </p>
          </div>
          <div className="mt-12 grid gap-6 md:grid-cols-3">
            {features.map((f, idx) => {
              const Icon = f.icon;
              return (
                <article
                  key={idx}
                  className="bg-white border border-slate-200 rounded-md p-6"
                  data-testid={`feature-card-${idx}`}
                >
                  <span
                    className="inline-flex h-11 w-11 items-center justify-center rounded-md"
                    style={{ backgroundColor: "#0B1D3A" }}
                  >
                    <Icon className="h-5 w-5 text-white" />
                  </span>
                  <h3
                    className="mt-5 text-xl text-slate-900"
                    style={{ fontFamily: "Merriweather, serif", fontWeight: 700 }}
                  >
                    {f.title}
                  </h3>
                  <p className="mt-3 text-sm leading-relaxed text-slate-700">
                    {f.body}
                  </p>
                </article>
              );
            })}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section
        className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20"
        data-testid="testimonials-section"
      >
        <h2
          className="text-3xl sm:text-4xl text-slate-900 max-w-3xl"
          style={{ fontFamily: "Merriweather, serif", fontWeight: 700 }}
        >
          {t("home.testimonials.title")}
        </h2>
        <div className="mt-10 grid gap-8 md:grid-cols-2">
          {["one", "two"].map((k, idx) => (
            <figure
              key={k}
              className="bg-white border border-slate-200 rounded-md p-8"
              data-testid={`testimonial-${idx}`}
            >
              <blockquote className="text-slate-800 text-lg leading-relaxed">
                “{t(`home.testimonials.${k}.quote`)}”
              </blockquote>
              <figcaption className="mt-6 flex items-center gap-4">
                <img
                  src={idx === 0 ? womanImg : heroImg}
                  alt=""
                  className="h-14 w-14 rounded-full object-cover border border-slate-200"
                />
                <div>
                  <div
                    className="text-slate-900"
                    style={{ fontFamily: "Merriweather, serif", fontWeight: 700 }}
                  >
                    {t(`home.testimonials.${k}.author`)}
                  </div>
                  <div className="text-sm text-slate-600">
                    {t(`home.testimonials.${k}.role`)}
                  </div>
                </div>
              </figcaption>
            </figure>
          ))}
        </div>
      </section>

      {/* Newsletter / CTA */}
      <section
        id="newsletter"
        className="relative"
        data-testid="newsletter-section"
      >
        <div
          className="absolute inset-0"
          style={{
            backgroundImage: `linear-gradient(rgba(11,29,58,0.85), rgba(11,29,58,0.85)), url(${limaImg})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
          aria-hidden="true"
        />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 grid lg:grid-cols-2 gap-12 items-center">
          <div className="text-white">
            <h2
              className="text-3xl sm:text-4xl"
              style={{ fontFamily: "Merriweather, serif", fontWeight: 700 }}
              data-testid="cta-title"
            >
              {t("home.cta_platform_title")}
            </h2>
            <p className="mt-5 text-slate-200 leading-relaxed max-w-xl">
              {t("home.cta_platform_body")}
            </p>
          </div>

          <form
            onSubmit={onSubmit}
            noValidate
            className="bg-white rounded-md p-8 border border-slate-200 shadow-xl"
            data-testid="newsletter-form"
          >
            <h3
              className="text-2xl text-slate-900"
              style={{ fontFamily: "Merriweather, serif", fontWeight: 700 }}
            >
              {t("home.newsletter.title")}
            </h3>
            <p className="mt-2 text-sm text-slate-600">
              {t("home.newsletter.subtitle")}
            </p>

            <div className="mt-6">
              <label
                htmlFor="nl-name"
                className="block text-sm font-medium text-slate-800"
              >
                {t("home.newsletter.name_label")}
              </label>
              <input
                id="nl-name"
                name="name"
                type="text"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder={t("home.newsletter.name_placeholder")}
                className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2.5 text-slate-900 outline-none focus:ring-2 focus:ring-[#10B981] focus:border-[#10B981]"
                data-testid="newsletter-name-input"
                required
              />
              {errors.name && (
                <p className="mt-1 text-xs text-red-600" data-testid="newsletter-name-error">
                  {errors.name}
                </p>
              )}
            </div>

            <div className="mt-4">
              <label
                htmlFor="nl-email"
                className="block text-sm font-medium text-slate-800"
              >
                {t("home.newsletter.email_label")}
              </label>
              <input
                id="nl-email"
                name="email"
                type="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                placeholder={t("home.newsletter.email_placeholder")}
                className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2.5 text-slate-900 outline-none focus:ring-2 focus:ring-[#10B981] focus:border-[#10B981]"
                data-testid="newsletter-email-input"
                required
              />
              {errors.email && (
                <p className="mt-1 text-xs text-red-600" data-testid="newsletter-email-error">
                  {errors.email}
                </p>
              )}
            </div>

            <button
              type="submit"
              className="mt-6 w-full rounded-md py-3 text-white text-sm font-semibold"
              style={{ backgroundColor: "#10B981" }}
              data-testid="newsletter-submit"
            >
              {t("home.newsletter.submit")}
            </button>
            <p className="mt-4 text-xs text-slate-500 leading-relaxed">
              {t("home.newsletter.consent")}
            </p>
          </form>
        </div>
      </section>
    </div>
  );
}
